// components/common/Modal/UpgradeConfirmDialog.tsx
import { ReactElement } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import UpgradeIcon from '@mui/icons-material/TrendingUp';
import { SxProps, Theme } from '@mui/material/styles';
import { Modal } from './Modal';

// Penalty label mapping
const PENALTY_LABELS: Record<string, string> = {
  '1': 'Procedural Error - Minor',
  '2': 'Slow Play - Minor',
  '3': 'Deck Error - Minor',
  '4': 'Drawing Cards - Minor',
  '5': 'Unsporting Conduct - Minor',
  '6': 'Marked Cards - Minor',
  '7': 'Tardiness - Major',
  '8': 'Marked Cards - Major',
  '9': 'Marked Cards - Strict',
  '10': 'Deck Error - Major',
  '11': 'Procedural Error - Major',
  '12': 'Procedural Error - Strict',
  '13': 'Drawing Cards - Major',
  '14': 'Unsporting Conduct - Major',
  '15': 'Unsporting Conduct - Cheating',
  '16': 'Unsporting Conduct - Severe',
  '17': 'Upgrade',
};

const getPenaltyLabel = (type: string): string => PENALTY_LABELS[type] || `Penalty #${type}`;

const getSeverityColor = (type: string): string => {
  const t = parseInt(type);
  if (t <= 6) return '#FFE600'; // Warning
  if (t === 7 || t === 8 || t === 10 || t === 11 || t === 13 || t === 14) return '#FF6B35'; // Game Loss
  if (t === 9 || t === 12) return '#FF3366'; // Match Loss
  return '#FF0000'; // DQ
};

export interface PenaltyHistoryItem {
  type?: string;
  penaltyType?: string;
  label?: string;
  round?: number | string;
  description?: string;
  issuedBy?: string;
}

export interface UpgradeConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  onApplyNormally: () => void;
  playerName?: string;
  penalties?: PenaltyHistoryItem[];
  currentPenaltyType?: string;
  currentPenaltyLabel?: string;
  similarCount?: number;
  loading?: boolean;
}

const buttonBaseStyle: SxProps<Theme> = {
  px: 3,
  py: 1,
  fontFamily: '"Rajdhani", sans-serif',
  fontWeight: 600,
  fontSize: '0.875rem',
  cursor: 'pointer',
  borderRadius: '8px',
  transition: 'all 0.3s ease',
  '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
};

/**
 * UpgradeConfirmDialog - Dialog for confirming potential penalty upgrade
 */
export function UpgradeConfirmDialog({
  open,
  onClose,
  onUpgrade,
  onApplyNormally,
  playerName = '',
  penalties = [],
  currentPenaltyType = '',
  currentPenaltyLabel = '',
  similarCount = 0,
  loading = false,
}: UpgradeConfirmDialogProps): ReactElement {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ color: '#FFE600' }} />
          Potential Upgrade Detected
        </Box>
      }
      titleColor="#FFE600"
      maxWidth="sm"
      showCloseButton={false}
      actions={
        <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Box
            component="button"
            onClick={onClose}
            disabled={loading}
            sx={{
              ...buttonBaseStyle,
              backgroundColor: 'transparent',
              border: 'none',
              color: '#8892A0',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)', color: '#FFFFFF' },
            }}
          >
            Cancel
          </Box>
          <Box
            component="button"
            onClick={onApplyNormally}
            disabled={loading}
            sx={{
              ...buttonBaseStyle,
              backgroundColor: 'rgba(0, 255, 212, 0.15)',
              border: '1px solid #00FFD4',
              color: '#00FFD4',
              '&:hover': {
                backgroundColor: 'rgba(0, 255, 212, 0.25)',
                boxShadow: '0 4px 15px rgba(0, 255, 212, 0.4)',
              },
            }}
          >
            Apply Normally
          </Box>
          <Box
            component="button"
            onClick={onUpgrade}
            disabled={loading}
            sx={{
              ...buttonBaseStyle,
              backgroundColor: 'rgba(255, 107, 53, 0.15)',
              border: '1px solid #FF6B35',
              color: '#FF6B35',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(255, 107, 53, 0.25)',
                boxShadow: '0 4px 15px rgba(255, 107, 53, 0.4)',
              },
            }}
          >
            <UpgradeIcon sx={{ fontSize: 18 }} />
            Submit for Upgrade
          </Box>
        </Box>
      }
    >
      {/* Player Info */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ color: '#8892A0', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1 }}>
          Player
        </Typography>
        <Typography sx={{ color: '#FFFFFF', fontSize: '1.25rem', fontWeight: 600, fontFamily: '"Rajdhani", sans-serif' }}>
          {playerName}
        </Typography>
      </Box>

      {/* Warning Message */}
      <Box sx={{
        backgroundColor: 'rgba(255, 230, 0, 0.1)',
        border: '1px solid rgba(255, 230, 0, 0.3)',
        borderRadius: '8px',
        p: 2,
        mb: 3,
      }}>
        <Typography sx={{ color: '#FFE600', fontWeight: 600, mb: 1 }}>
          This player has {similarCount} similar penalty(ies)
        </Typography>
        <Typography sx={{ color: '#FFFFFF', fontSize: '0.875rem' }}>
          Current penalty: <strong>{currentPenaltyLabel || getPenaltyLabel(currentPenaltyType)}</strong>
        </Typography>
        <Typography sx={{ color: '#8892A0', fontSize: '0.8rem', mt: 1 }}>
          If this is the same infraction, it should be submitted for upgrade. If it's a different scenario within the same category, apply normally.
        </Typography>
      </Box>

      {/* Penalty History */}
      {penalties.length > 0 && (
        <Box>
          <Typography sx={{ color: '#8892A0', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>
            Penalty History
          </Typography>
          <Box sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
            borderRadius: '8px',
            maxHeight: 200,
            overflowY: 'auto',
          }}>
            {penalties.map((penalty, idx) => {
              const penaltyType = penalty.type || penalty.penaltyType || '';
              return (
                <Box
                  key={idx}
                  sx={{
                    p: 1.5,
                    borderBottom: idx < penalties.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box>
                    <Typography sx={{ color: '#FFFFFF', fontSize: '0.875rem' }}>
                      {penalty.label || getPenaltyLabel(penaltyType)}
                    </Typography>
                    <Typography sx={{ color: '#8892A0', fontSize: '0.75rem' }}>
                      {penalty.issuedBy ? `By ${penalty.issuedBy}` : `Round ${penalty.round || '?'}`} • {penalty.description || 'No description'}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={penaltyType === currentPenaltyType ? 'SAME TYPE' : `#${penaltyType}`}
                    sx={{
                      backgroundColor: penaltyType === currentPenaltyType
                        ? 'rgba(255, 107, 53, 0.2)'
                        : 'rgba(255, 255, 255, 0.1)',
                      color: penaltyType === currentPenaltyType
                        ? '#FF6B35'
                        : getSeverityColor(penaltyType),
                      fontWeight: 600,
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Modal>
  );
}

export default UpgradeConfirmDialog;
